// ============================================================
//  DareDino — Client-Side Anti-Cheat / Anti-Tamper Utilities
// ============================================================
//  1. HMAC-SHA256 score signing (verified server-side)
//  2. IIFE-style closure protection for game variables (React state is
//     already closure-scoped; nothing is attached to window)
//  3. Right-click + DevTools shortcut blocking
//  4. Opt-in anti-debug loop (production only)
//
//  NOTE: Client-side anti-cheat is casual protection. A determined
//  attacker with dev tools can always bypass this. Real security must
//  live on the SERVER (see /api/scores hash + velocity checks).
// ============================================================

// Must match GAME_SECRET on the backend. If you rotate, update both.
// (This is still visible in the JS bundle — that's OK, it just raises
// the bar for casual tampering. The server-issued NONCE is what makes
// each submission unforgeable across sessions.)
// Reads from REACT_APP_GAME_SECRET if set; falls back to the default so a
// missing env value never breaks score submission.
const CLIENT_SECRET =
  process.env.REACT_APP_GAME_SECRET || "daredino-hmac-secret-2026-v1-rotate-me";

// ------------------------------------------------------------
//  HMAC-SHA256 helper — pure Web Crypto, no dependencies
// ------------------------------------------------------------
async function hmacSha256(message, secret = CLIENT_SECRET) {
  const enc = new TextEncoder();
  const keyData = enc.encode(secret);
  const msgData = enc.encode(message);
  const key = await window.crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const sig = await window.crypto.subtle.sign("HMAC", key, msgData);
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// Sign a score submission — the message format MUST match the server.
//   server side: hmac_sha256(f"{score}:{start_time}:{end_time}:{nonce}:{obstacles_passed}", GAME_SECRET)
// obstaclesPassed is required — the server rejects the score if the HMAC
// message doesn't include it. Pass 0 if the game genuinely tracked none
// (short runs are still valid, but the field must be present).
export async function signScore({ score, startTime, endTime, nonce, obstaclesPassed }) {
  const obs = Number.isFinite(obstaclesPassed) ? Math.max(0, Math.floor(obstaclesPassed)) : 0;
  const message = `${score}:${startTime}:${endTime}:${nonce}:${obs}`;
  return hmacSha256(message);
}

// ------------------------------------------------------------
//  reCAPTCHA v3 — silent bot-scoring (only if site key configured)
// ------------------------------------------------------------
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

let recaptchaReady = false;
export function loadRecaptcha() {
  if (!RECAPTCHA_SITE_KEY || recaptchaReady) return;
  const s = document.createElement("script");
  s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
  recaptchaReady = true;
}

export async function getRecaptchaToken(action = "submit_score") {
  if (!RECAPTCHA_SITE_KEY) return null;
  return new Promise((resolve) => {
    if (!window.grecaptcha) return resolve(null);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action })
        .then((token) => resolve(token))
        .catch(() => resolve(null));
    });
  });
}

// ------------------------------------------------------------
//  Anti-Tamper: block right-click and DevTools shortcuts
// ------------------------------------------------------------
let antiTamperInstalled = false;

export function installAntiTamper({ antiDebug = false } = {}) {
  if (antiTamperInstalled || typeof window === "undefined") return;
  antiTamperInstalled = true;

  // (1) Block context menu (right-click) — casual defense
  window.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    return false;
  });

  // (2) Block common DevTools shortcuts
  window.addEventListener("keydown", (e) => {
    const key = (e.key || "").toLowerCase();
    // F12
    if (e.key === "F12") {
      e.preventDefault();
      return false;
    }
    // Ctrl/Cmd + Shift + I  (inspector)
    // Ctrl/Cmd + Shift + J  (console)
    // Ctrl/Cmd + Shift + C  (element inspector)
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && ["i", "j", "c"].includes(key)) {
      e.preventDefault();
      return false;
    }
    // Ctrl/Cmd + U  (view source)
    if ((e.ctrlKey || e.metaKey) && key === "u") {
      e.preventDefault();
      return false;
    }
    // Ctrl/Cmd + S  (save page)
    if ((e.ctrlKey || e.metaKey) && key === "s") {
      e.preventDefault();
      return false;
    }
  });

  // (3) Block text drag/select for the whole app? Skip — hurts UX.

  // (4) Anti-debug loop — only in production if antiDebug=true, otherwise
  //     it fires every second and interferes with local dev.
  if (antiDebug && process.env.NODE_ENV === "production") {
    setInterval(() => {
      const start = performance.now();
      // eslint-disable-next-line no-debugger
      debugger;
      const elapsed = performance.now() - start;
      if (elapsed > 100) {
        // DevTools likely open — nudge the user. We don't hard-block
        // legitimate users (React DevTools users, screen readers, QA)
        // but you can uncomment the next line for a harder redirect:
        // window.location.replace("about:blank");
        console.clear();
      }
    }, 1000);
  }
}
